import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import countryNames from '../data/countryNames.json';

/** Filtered country picker: type to narrow (e.g. "I…" → India, Indonesia, …). */
export default function CountryCombobox({ id, label, required, value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => {
    setQ(value || '');
  }, [value]);

  const sortedCountries = useMemo(() => {
    const others = countryNames.filter(c => c !== "India");
    return ["India", ...others];
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sortedCountries.slice(0, 500);
    // Prioritize startsWith, then includes
    const starts = sortedCountries.filter((n) => n.toLowerCase().startsWith(needle));
    const includes = sortedCountries.filter((n) => n.toLowerCase().includes(needle) && !n.toLowerCase().startsWith(needle));
    return [...starts, ...includes].slice(0, 80);
  }, [q, sortedCountries]);

  const pick = useCallback(
    (name) => {
      onChange(name);
      setQ(name);
      setOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showError = !!error || (required && q && !countryNames.includes(q));

  return (
    <div className={`field ${showError ? 'field-has-error' : ''}`} ref={wrapRef}>
      {label && (
        <label htmlFor={id} style={{ fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.04em' }}>
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          name={`search-${id}`}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          required={required}
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            // If the user clears the input, clear the selection
            if (!v) onChange('');
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Type to search (e.g. Ind…)"
          aria-expanded={open}
          aria-controls={id ? `${id}-country-list` : undefined}
          aria-autocomplete="list"
          style={{
            borderRadius: '12px',
            height: '3.25rem',
            ...(showError ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' } : undefined)
          }}
        />
        {open && filtered.length > 0 && (
          <ul
            id={id ? `${id}-country-list` : undefined}
            role="listbox"
            className="country-combobox-list"
          >
            {filtered.map((name) => (
              <li key={name}>
                <button type="button" role="option" className="country-combobox-opt" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(name)}>
                  {name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <span className="field-error-msg">{error}</span>}
      {required && q && !countryNames.includes(q) && !error && (
        <span className="field-error-msg">Pick a country from the suggestions or spell it exactly.</span>
      )}
    </div>
  );
}
